/**
 * The inspection window, derived from when the photos were actually taken.
 *
 * WHY THIS EXISTS
 * ---------------
 * The cover sheet needs an inspection start and end time. The QA rule
 * `photo-timestamps` compares the photos against whatever window the report
 * carries and reports a mismatch — which reads as a compliance finding, and
 * became one of the most common things reviewers had to chase.
 *
 * But the photos are the BETTER evidence. An inspector's typed times are a
 * memory; EXIF capture times are a record. So the disagreement is not really
 * a compliance question, it is an arithmetic one: the photos already say when
 * the inspection happened, and we can hand the reviewer that answer instead
 * of asking them to reconcile two numbers by hand.
 *
 * THE PADDING
 * -----------
 * The first photo is not the start of the inspection — the inspector arrived,
 * met the owner, and set up first. The last photo is not the end either, but
 * the gap is shorter (pack up and go). Hence a lead-in of 10 minutes and a
 * tail-out of 5. These are deliberate, adjustable estimates, and everything
 * that renders a derived window says so — a derived time must never be
 * presented as if the inspector recorded it.
 *
 * TIMESTAMP CONVENTIONS (see InspectionCoverSheetSchema)
 * -----------------------------------------------------
 * Values carrying `Z`/`±HH:MM` name an instant; naive values are the
 * capturing device's LOCAL wall clock. The cover sheet wants local time
 * because the CER portal does, so a derived window is emitted naive when we
 * know the device offset, and as a `Z` instant when we do not — never a
 * silent guess about which timezone a bare string meant.
 */
/**
 * Fewest photo timestamps that can describe a real inspection.
 *
 * A single-photo inspection does not happen: an inspector photographs the
 * switchboard, the nameplates and every finding, so one timestamp means the
 * photo dump has not landed yet — not that the visit took no time. An
 * estimate resting on fewer than this is reported as INSUFFICIENT rather
 * than presented as an answer, because a zero-length window on a
 * regulator-facing report is worse than a blank one.
 */
export const MIN_PHOTOS_FOR_WINDOW = 2;
/** Minutes before the first photo that the inspection is assumed to have started. */
export const INSPECTION_LEAD_IN_MINUTES = 10;
/** Minutes after the last photo that the inspection is assumed to have ended. */
export const INSPECTION_TAIL_OUT_MINUTES = 5;
/** A trailing `Z` or `±HH:MM` — i.e. the string names an instant, not a wall clock. */
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;
/**
 * Resolve a cover-sheet or photo timestamp to an instant (epoch ms).
 *
 * Naive values are the device's local wall clock, so they resolve against
 * `offsetMinutes` rather than whatever timezone the reader happens to run in
 * — Lambda runs in UTC, and without this an AEST inspection lands 10 hours
 * out. Falls back to reading naive values as UTC when no offset is known,
 * which is what submissions predating `deviceUtcOffsetMinutes` already got.
 */
export function parseTimestampToInstant(value, offsetMinutes) {
    if (!value)
        return undefined;
    if (HAS_TIMEZONE.test(value)) {
        const ms = Date.parse(value);
        return Number.isNaN(ms) ? undefined : ms;
    }
    // A naive DATE-TIME (`2026-08-25T10:10:00`) is parsed as LOCAL time by
    // `Date.parse` per spec — which would resolve the same report differently
    // in Lambda (UTC) than in a reviewer's browser (AEST). Anchor it to UTC
    // explicitly, then shift by the device offset when we know it. Date-only
    // forms are already UTC per spec, so they are left alone.
    const anchored = value.includes('T') ? `${value}Z` : value;
    const ms = Date.parse(anchored);
    if (Number.isNaN(ms))
        return undefined;
    return offsetMinutes === undefined ? ms : ms - offsetMinutes * 60_000;
}
/**
 * Render an instant the way the cover sheet expects: local wall clock when
 * the device offset is known, otherwise an explicit UTC instant.
 */
export function formatInstantForCover(instantMs, offsetMinutes) {
    if (offsetMinutes === undefined)
        return new Date(instantMs).toISOString();
    return new Date(instantMs + offsetMinutes * 60_000).toISOString().slice(0, 19);
}
/**
 * Estimate the inspection window from photo capture times.
 *
 * Returns undefined when no photo carries a readable timestamp — there is
 * nothing to derive from, and inventing a window would be worse than leaving
 * it blank on a regulator-facing report.
 */
export function estimateInspectionWindow(input) {
    const offset = input.deviceUtcOffsetMinutes;
    const leadIn = input.leadInMinutes ?? INSPECTION_LEAD_IN_MINUTES;
    const tailOut = input.tailOutMinutes ?? INSPECTION_TAIL_OUT_MINUTES;
    const instants = input.capturedAts
        .map((value) => parseTimestampToInstant(value, offset))
        .filter((ms) => ms !== undefined);
    if (instants.length === 0)
        return undefined;
    const { kept, dropped } = keepDominantDay(instants, offset);
    const sorted = [...kept].sort((a, b) => a - b);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return {
        startedAt: formatInstantForCover(first - leadIn * 60_000, offset),
        endedAt: formatInstantForCover(last + tailOut * 60_000, offset),
        photoCount: sorted.length,
        firstPhotoAt: formatInstantForCover(first, offset),
        lastPhotoAt: formatInstantForCover(last, offset),
        outliersDropped: dropped,
        sufficient: sorted.length >= MIN_PHOTOS_FOR_WINDOW && last > first,
    };
}
/**
 * Keep only the calendar day holding the most photos.
 *
 * Days are counted in the device's local time, since that is the day the
 * inspector would name. Ties keep the earliest day, so the result is stable
 * rather than dependent on Map iteration order.
 */
function keepDominantDay(instants, offsetMinutes) {
    if (instants.length <= 1)
        return { kept: [...instants], dropped: 0 };
    const byDay = new Map();
    for (const ms of instants) {
        const day = new Date(ms + (offsetMinutes ?? 0) * 60_000).toISOString().slice(0, 10);
        const bucket = byDay.get(day) ?? [];
        bucket.push(ms);
        byDay.set(day, bucket);
    }
    let best;
    for (const [, bucket] of [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        if (!best || bucket.length > best.length)
            best = bucket;
    }
    if (!best)
        return { kept: [...instants], dropped: 0 };
    return { kept: best, dropped: instants.length - best.length };
}
/**
 * The calendar day an inspection happened, read from its photos.
 *
 * The failsafe for a report that reached us without `cover.inspectionDate`.
 * That date starts the ten-day upload clock, so a report missing it is not
 * merely untidy — it is invisible to the obligation, which is worse than
 * being late. The photos always know: they were taken on the day.
 *
 * Returns the DOMINANT local day (see `keepDominantDay`), not the padded
 * window start — a 7am inspection padded by the lead-in would otherwise
 * report the previous day.
 */
export function inspectionDateFromPhotos(input) {
    const offset = input.deviceUtcOffsetMinutes;
    const instants = input.capturedAts
        .map((value) => parseTimestampToInstant(value, offset))
        .filter((ms) => ms !== undefined);
    if (instants.length === 0)
        return undefined;
    const { kept } = keepDominantDay(instants, offset);
    const earliest = Math.min(...kept);
    return formatInstantForCover(earliest, offset).slice(0, 10);
}
//# sourceMappingURL=inspection-window.js.map